import React from "react";
import styled, { css } from "styled-components";
import { toast } from "react-toastify";
import _ from "lodash";

import { Text } from "src/library/components/Text";
import {
  useDashboardApi,
  IDashboard,
  GetDashboardsFilters
} from "src/core/dashboard/api/dashboard.api";
import { PaginatedParams } from "src/library/utils";
import { useCurrentUser } from "src/core/users/hooks";
import { LoadingSpinner } from "src/library/components/LoadingSpinner";
import { Icons } from "src/library/components/Icon/types";
import Icon from "src/library/components/Icon/Icon";
import {
  DashboardMode,
  useDashboardContext
} from "src/core/dashboard/contexts/DashboardContext";
import cacheMinutesToMilliseconds from "src/library/utils/time-helpers/cache-minutes-to-milliseconds";

import DashboardSwitcherOptions from "./DashboardSwitcherOptions";
import { CreateDashboardModal } from "./CreateDashboardModal";

export interface DashboardSwitcherProps {
  onDashboardSwitch: () => void;
}

const defaultPagination: PaginatedParams<IDashboard> = {
  page: 0,
  limit: 10,
  offset: 0,
  sort: [ [ "name", "ASC" ] ]
};

export const GET_DAHSBOARDS_CACHE_KEY = "user-dashboards";

/**
 * Component for switching between active dashboard
 */
const DashboardSwitcher: React.FunctionComponent<DashboardSwitcherProps> = ({ onDashboardSwitch }) => {
  const {
    currentDashboard, updateCurrentDashboard, mode
  } = useDashboardContext();

  const [ dashboardsResponse, getDashboards ] = useDashboardApi("GET_DASHBOARDS");
  const [ dashboards, setDashboards ] = React.useState<IDashboard[]>([]);
  const [ showOptions, setShowOptions ] = React.useState(false);
  const [ showCreateDashboardModal, setShowCreateDashboardModal ] = React.useState(false);
  const [ { currentUser } ] = useCurrentUser();

  React.useEffect(() => {
    if (dashboardsResponse.error) {
      toast.error(dashboardsResponse.error.message);
    }

    if (dashboardsResponse.data?.result) {
      setDashboards(dashboardsResponse.data.result);
    }
  }, [
    dashboardsResponse,
    dashboardsResponse.data?.result,
    dashboardsResponse.error
  ]);

  // handler for managing requests for dashboards
  const handleFetchDashboards = React.useCallback((searchValue: string, clearCache = false) => {
    if (currentUser?.id) {
      const dashboardFilters: GetDashboardsFilters = { userId: currentUser.id };

      if (searchValue.length > 0) {
        dashboardFilters.name = searchValue;

        getDashboards({
          pagination: defaultPagination,
          filters: dashboardFilters
        });
      } else {
        getDashboards({
          pagination: defaultPagination,
          filters: dashboardFilters
        }, {
          cache: {
            maxAge: cacheMinutesToMilliseconds(60),
            key: () => GET_DAHSBOARDS_CACHE_KEY,
            ignoreCache: clearCache
          }
        });
      }
    }
  }, [ currentUser?.id, getDashboards ]);

  // fetch dashboards on mount to preload options
  React.useEffect(() => {
    handleFetchDashboards("");
  }, [ handleFetchDashboards ]);

  React.useEffect(() => {
    if (showOptions) {
      handleFetchDashboards("");
    }
  }, [ showOptions, handleFetchDashboards ]);

  const handleCreateDashboard = React.useCallback(() => {
    setShowCreateDashboardModal(true);
    setShowOptions(false);
  }, []);

  return (
    <StyledWrapper className={mode === DashboardMode.Edit ? "disabled" : undefined}>
      {dashboardsResponse.pending || !currentDashboard ? (
        <div className="loading-container">
          <LoadingSpinner
            type="flat"
            position="inline"
            size={8}
          />
        </div>
      ) : (
        <button
          className="display-name"
          onClick={() => setShowOptions(!showOptions)}
        >
          <Text as="h1">
            {currentDashboard ? _.startCase(currentDashboard.name) : "Please Select Dashboard"}
          </Text>

          <Icon
            className="search-icon"
            icon={Icons.CHEVRONDOWN}
            $fillColor="primaryDark"
          />
        </button>
      )}

      <DashboardSwitcherOptions
        open={showOptions}
        selectedDashboard={currentDashboard}
        availableDashboards={dashboards}
        getDashboardsLoading={dashboardsResponse.pending}
        totalDashboards={dashboardsResponse.data?.count}
        fetchDashboards={handleFetchDashboards}
        onDismiss={() => setShowOptions(false)}
        onSelectDashboard={dashboard => {
          updateCurrentDashboard(dashboard);
          onDashboardSwitch();
        }}
        onCreateDashboard={handleCreateDashboard}
      />

      <CreateDashboardModal
        open={showCreateDashboardModal}
        onDismiss={() => setShowCreateDashboardModal(false)}
      />
    </StyledWrapper>
  );
};

const StyledWrapper = styled.div`
  ${({ theme }) => css`
    margin-bottom: ${theme.spacing.margins.md};

    button {
      background-color: inherit;
    }
    
    .loading-container {
      display: flex;
      justify-content: center;
      align-items: center;
      width: 100%;
      height: 55px;
    }

    .display-name {
      display: flex;
      align-items: center;
      border: none;

      h1:hover,
      svg:hover {
        cursor: pointer;
      }

      h1 {
        margin: 0;
        padding-right: ${theme.spacing.padding.md};
      }
    }

    &.disabled {
      opacity: 0.5;
      pointer-events: none;
    }
  `}
`;

export default DashboardSwitcher;
